import { useCallback, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type { TableState } from 'components/data-table/types';
import type {
  InventoryCandidateArea,
  InventoryFormationCandidateRow,
  InventoryFormationCandidatesResponse,
} from 'types/api-responses';

export const INVENTORY_FORMATION_CANDIDATES_KEY =
  'inventory-formation-candidates';

// No leading slash: `api` sets a ky prefixUrl and ky throws on absolute paths
// (lib/request/api.ts).
const CANDIDATES_PATH = 'queries/inventory_formation_candidates';

export const CANDIDATES_DEFAULT_PAGE_SIZE = 25;

/**
 * The PACKAGE anchor: instead of a type curve to launch from, the
 * server derives the candidate set from a title package — every Type Curve Area
 * its assets intersect, with LTD/unit counts scoped to the package's own units.
 * Same response shape, same rules, different anchor.
 */
export interface PackageCandidatesAnchor {
  packageId: number;
  typeCurveScenarioId: number;
  /** false skips the area polygons — for callers that only read the summary. */
  includeGeometry?: boolean;
  /**
   * The run the page is showing, when its picks still match the run: the response then carries
   * that run's valued count per package (`calculationValued`). Omit for the live count only.
   */
  calculationId?: number | null;
}

interface FormationCandidates {
  rows: InventoryFormationCandidateRow[];
  totalCount: number;
  /** The map layer's areas, target first — inline GeoJSON, no separate fetch. */
  areas: InventoryCandidateArea[];
  /** `[minLng, minLat, maxLng, maxLat]` for the whole candidate set. */
  bbox: [number, number, number, number] | null;
  /** The server's footer rollup — carries the package anchor's scoped LTD figures. */
  summary: InventoryFormationCandidatesResponse['summary'] | null;
  isLoading: boolean;
  error: Error | null;
  /** One handler for every server-driven table change (page, size, search). */
  loadData: (state: TableState) => void;
}

/**
 * The (area, formation) pairs a workspace can cover, with the type curves
 * available for each.
 *
 * Two anchors. The LAUNCH anchor: the server resolves the scenario and
 * applicable area configs from the launched type curve, so the only input is
 * its `uniqueTcId`; the launched pair comes back `isOrigin`, and one type curve
 * per pair comes back `isDefault`. The PACKAGE anchor
 * (`options.packageAnchor`): the server derives areas and units from the
 * package instead — no origin, counts scoped to the package's units. When the
 * package anchor is given, `uniqueTcId` is ignored.
 *
 * Server-paginated, like the listing views: the modal selects pairs explicitly,
 * so it never needs pairs it has not shown.
 *
 * @param {string | null | undefined} uniqueTcId - The type curve the workspace launches from.
 * @param {object} options - Gate the fetch (the modal fetches on open), and optionally the
 *   package anchor.
 * @param {boolean} options.enabled - Whether the query may run.
 * @param {PackageCandidatesAnchor | null} [options.packageAnchor] - The package anchor; when
 *   given, `uniqueTcId` is ignored.
 * @return {FormationCandidates} Rows, count, summary and the table's paging handler.
 */
export function useInventoryFormationCandidates(
  uniqueTcId: string | null | undefined,
  options: { enabled: boolean; packageAnchor?: PackageCandidatesAnchor | null }
): FormationCandidates {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(CANDIDATES_DEFAULT_PAGE_SIZE);
  // Server-side search text (the table's global filter), sent as `text` — the
  // same shape the Units / Wells tables use.
  const [text, setText] = useState('');

  const anchor = options.packageAnchor ?? null;
  const live = options.enabled && (anchor !== null || Boolean(uniqueTcId));

  const query = useQuery<InventoryFormationCandidatesResponse, Error>({
    queryKey: [
      INVENTORY_FORMATION_CANDIDATES_KEY,
      anchor
        ? [
            'package',
            anchor.packageId,
            anchor.typeCurveScenarioId,
            anchor.includeGeometry ?? true,
            anchor.calculationId ?? null,
          ]
        : uniqueTcId,
      page,
      pageSize,
      text,
    ],
    queryFn: ({ signal }) =>
      api
        .post(CANDIDATES_PATH, {
          json: {
            query: {
              ...(anchor
                ? {
                    packageId: anchor.packageId,
                    typeCurveScenarioId: anchor.typeCurveScenarioId,
                    ...(anchor.includeGeometry === false
                      ? { includeGeometry: false }
                      : {}),
                    ...(anchor.calculationId
                      ? { calculationId: anchor.calculationId }
                      : {}),
                  }
                : { uniqueTcId }),
              page,
              pageSize,
              ...(text ? { text } : {}),
            },
          },
          signal,
        })
        .json<InventoryFormationCandidatesResponse>(),
    enabled: live,
    placeholderData: keepPreviousData,
    staleTime: 300_000,
  });

  const loadData = useCallback((state: TableState) => {
    setPage(state.page);
    setPageSize(state.pageSize);
    setText(state.globalFilter ?? '');
  }, []);

  return {
    rows: query.data?.results ?? [],
    totalCount: query.data?.totalCount ?? 0,
    areas: query.data?.areas ?? [],
    bbox: query.data?.bbox ?? null,
    summary: query.data?.summary ?? null,
    // React Query reports `isPending: true` for a disabled query, so this is
    // gated on the query actually being live — otherwise the table sits in a
    // permanent skeleton before a type curve is chosen.
    isLoading: live && query.isPending,
    error: query.error,
    loadData,
  };
}
